import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiStar, FiChevronLeft, FiChevronRight, FiMessageCircle, FiUser } from 'react-icons/fi';
import './Testimonials.css';

const Testimonials = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [failedImages, setFailedImages] = useState(new Set());

  const testimonials = [
    {
      id: 1,
      name: "Sarah Johnson",
      position: "CEO",
      company: "TechStart Inc.",
      image: null,
      rating: 5,
      text: "RKube Inc transformed our financial operations completely. Their accounting services are top-notch and their team is incredibly responsive. We've seen a 40% improvement in our financial reporting efficiency.",
      service: "Financial Services"
    },
    {
      id: 2,
      name: "Michael Chen",
      position: "CFO",
      company: "Innovation Labs",
      image: null,
      rating: 5,
      text: "The IT solutions provided by RKube Inc revolutionized our workflow. Their custom software development exceeded our expectations and delivered exactly what we needed for our growing business.",
      service: "IT Solutions"
    },
    {
      id: 3,
      name: "Emily Rodriguez",
      position: "Operations Director",
      company: "Growth Dynamics",
      image: null,
      rating: 5,
      text: "Their staffing services helped us find exceptional talent quickly. The team understands our needs and consistently delivers high-quality candidates that fit our company culture perfectly.",
      service: "Staffing Services"
    },
    {
      id: 4,
      name: "David Rothchild",
      position: "Founder",
      company: "Digital Ventures",
      image: null,
      rating: 5,
      text: "RKube Inc's comprehensive approach to business process automation saved us countless hours. Their expertise in streamlining operations has been invaluable for our scaling business.",
      service: "Process Automation"
    },
    {
      id: 5,
      name: "Lisa Thompson",
      position: "Marketing Director",
      company: "Brand Solutions",
      image: null,
      rating: 5,
      text: "The digital marketing services exceeded our ROI expectations. Their strategic approach and data-driven campaigns helped us increase our online presence by 300% in just six months.",
      service: "Digital Marketing"
    }
  ];

  const getInitials = (name) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  const getAvatarColor = (name) => {
    const colors = [
      '#3B82F6', '#8B5CF6', '#10B981', '#F59E0B', 
      '#EF4444', '#06B6D4', '#84CC16', '#F97316'
    ];
    const hash = name.split('').reduce((acc, char) => char.charCodeAt(0) + acc, 0);
    return colors[hash % colors.length];
  };

  const handleImageError = (testimonialId) => {
    setFailedImages(prev => new Set([...prev, testimonialId]));
  };

  const nextTestimonial = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  useEffect(() => {
    if (!isAutoPlaying) return;
    
    const interval = setInterval(nextTestimonial, 5000);
    return () => clearInterval(interval);
  }, [isAutoPlaying]);

  const slideVariants = {
    enter: (direction) => ({
      x: direction > 0 ? 1000 : -1000,
      opacity: 0
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1
    },
    exit: (direction) => ({
      zIndex: 0,
      x: direction < 0 ? 1000 : -1000,
      opacity: 0
    })
  };

  return (
    <section className="testimonials">
      <div className="container">
        <motion.div 
          className="section-header"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <div className="section-badge">
            <FiStar />
            <span>Client Success Stories</span>
          </div>
          <h2 className="section-title">
            What Our <span className="gradient-text">Clients Say</span>
          </h2>
          <p className="section-subtitle">
            Don't just take our word for it - hear from businesses that have transformed with our services
          </p>
        </motion.div>

        <div className="testimonials-container">
          <div 
            className="testimonials-slider"
            onMouseEnter={() => setIsAutoPlaying(false)}
            onMouseLeave={() => setIsAutoPlaying(true)}
          >
            <AnimatePresence mode="wait" custom={currentIndex}>
              <motion.div
                key={currentIndex}
                custom={currentIndex}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{
                  x: { type: "spring", stiffness: 300, damping: 30 },
                  opacity: { duration: 0.2 }
                }}
                className="testimonial-slide"
              >
                <div className="testimonial-card">
                  <div className="quote-icon">
                    <FiMessageCircle />
                  </div>
                  
                  <div className="rating">
                    {[...Array(testimonials[currentIndex].rating)].map((_, i) => (
                      <FiStar key={i} className="star filled" />
                    ))}
                  </div>

                  <p className="testimonial-text">
                    "{testimonials[currentIndex].text}"
                  </p>

                  <div className="testimonial-author">
                    <div className="author-image">
                      {testimonials[currentIndex].image ? (
                        <img 
                          src={testimonials[currentIndex].image} 
                          alt={testimonials[currentIndex].name}
                          onError={() => handleImageError(testimonials[currentIndex].id)}
                        />
                      ) : (
                        <div className="fallback-avatar" style={{ backgroundColor: getAvatarColor(testimonials[currentIndex].name) }}>
                          {getInitials(testimonials[currentIndex].name)}
                        </div>
                      )}
                    </div>
                    <div className="author-info">
                      <h4 className="author-name">{testimonials[currentIndex].name}</h4>
                      <p className="author-position">
                        {testimonials[currentIndex].position} at {testimonials[currentIndex].company}
                      </p>
                      <span className="service-tag">
                        {testimonials[currentIndex].service}
                      </span>
                    </div>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>

            <div className="slider-controls">
              <button 
                className="slider-btn prev"
                onClick={prevTestimonial}
              >
                <FiChevronLeft />
              </button>
              
              <div className="slider-dots">
                {testimonials.map((_, index) => (
                  <button
                    key={index}
                    className={`dot ${index === currentIndex ? 'active' : ''}`}
                    onClick={() => setCurrentIndex(index)}
                  />
                ))}
              </div>

              <button 
                className="slider-btn next"
                onClick={nextTestimonial}
              >
                <FiChevronRight />
              </button>
            </div>
          </div>

          <motion.div 
            className="testimonials-stats"
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="stat-item">
              <h3 className="gradient-text">98%</h3>
              <p>Client Satisfaction</p>
            </div>
            <div className="stat-item">
              <h3 className="gradient-text">500+</h3>
              <p>Projects Completed</p>
            </div>
            <div className="stat-item">
              <h3 className="gradient-text">5.0</h3>
              <p>Average Rating</p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials; 